/**
 * Route guard for modules (G10). A gated path (shell/nav.ts GATED_ROUTES) redirects to the dashboard once lib/flags
 * moduleState says 'hide': an audit-only workspace without that module, or a module whose API enforces its flag (AI
 * search) with the flag off. While the flags load it renders the page (the API still enforces access; this only keeps
 * hidden modules out of the way), so a workspace with the module never sees a flash of the dashboard.
 */
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { moduleState, useFlags, type FlagState } from '../../../lib/flags';
import { GATED_ROUTES } from '../../../shell/nav';

/** 'show' | 'hide' | 'wait' for a path given the flag states. */
export function gateFor(path: string, flag: (k: string) => FlagState): 'show' | 'hide' | 'wait' {
  const m = GATED_ROUTES[path];
  return m ? moduleState(m, flag) : 'show';
}

export function ModuleGate({ path, children }: { path: string; children: ReactNode }) {
  const flag = useFlags();
  return gateFor(path, flag) === 'hide' ? <Navigate to="/" replace /> : <>{children}</>;
}

/** Search hits and links into hidden modules are dropped (G10). */
export function visiblePath(path: string, flag: (k: string) => FlagState): boolean {
  const base = Object.keys(GATED_ROUTES).filter((p) => path === p || path.startsWith(`${p}/`) || path.startsWith(`${p}?`)).sort((a, b) => b.length - a.length)[0];
  return !base || gateFor(base, flag) === 'show';
}
